import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import Avatar from "../../assets/svg/avatar.svg";
import daburLogo from "../../assets/svg/appIcon.svg";
import InfiniteScroll from "react-infinite-scroll-component";
import Markdown from "markdown-to-jsx";
import classNames from "classnames";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  getChatBySessionId,
  getDocumentSessionId,
  getInboxChatBySessionId,
  resetVoice,
  sendChat,
  setChatSessionId,
  setIsMicOn,
  setPage,
  setQueryMessage,
  setSelectedChat,
  uploadFiles,
} from "../../lib/redux/actions";
import TextInput from "../ui/TextInput"; 
import { useDispatch, useSelector } from "react-redux";
import toast from "../../lib/utils/toast";
import SkeletonLoader from "../../lib/common/components/loader/SkeletonLoader";
import MessageLoader from "../../lib/common/components/loader/MessageLoader";
import BotMarkdown from "../../lib/common/components/markdown/BotMarkdown";
import { CustomLink, preprocessText } from "../../lib/utils/helper";
import WelcomeInterface from "./WelcomeInterface";
import FileCard from "./FileCard";
import UploadFileCard from "../ui/UploadFileCard";
import { Copy, Check, Mic, Download, ChevronDown } from "lucide-react";
import CustomTooltip from "../ui/Tooltip";
import VoiceModeDialog from "../voiceMode/VoiceModeDialog";
import MainContent from "../voiceMode/MainContent";
import BottomControls from "../voiceMode/ButtonControls";
import useVoiceActions from "../voiceMode/useVoiceAction";
import { useLocation, useOutletContext } from "react-router-dom";
import ReadOnlyChat from "./ReadOnlyChat";
import { trackEvent, trackException } from "../../lib/utils/telemetryService";
import { MESSAGES } from "../../config/messages";
import { getModelData } from "../../lib/utils/utils";


/**
 * Validates CSV/XLSX files for consistent row structure (same column count per row).
 * Prevents ParserError: "Expected X fields in line N, saw Y" from backend.
 */
const validateCsvOrXlsxFile = (file) => {
  return new Promise((resolve) => {
    const isCsv = file.type === "text/csv";
    const isXlsx =
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isCsv && !isXlsx) {
      resolve({ valid: true });
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        if (isCsv) {
          const text = e.target.result;
          const result = Papa.parse(text, {
            header: false,
            skipEmptyLines: true,
          });

          if (result.errors?.length > 0) {
            resolve({ valid: false, error: "Invalid CSV structure" });
            return;
          }

          const rows = result.data;
          if (!rows || rows.length === 0) {
            resolve({ valid: true });
            return;
          }

          const expectedColCount = rows[0].length;
          if (expectedColCount === 0) {
            resolve({ valid: true });
            return;
          }
          for (let i = 1; i < rows.length; i++) {
            if (rows[i].length !== expectedColCount) {
              resolve({ valid: false, error: "Invalid CSV structure" });
              return;
            }
          }
        } else if (isXlsx) {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          if (!sheet) {
            resolve({ valid: true });
            return;
          }

          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
          if (!rows || rows.length === 0) {
            resolve({ valid: true });
            return;
          }

          const firstRowCols = (rows[0] || []).length;
          if (firstRowCols === 0) {
            resolve({ valid: true });
            return;
          }

          for (let i = 1; i < rows.length; i++) {
            const row = rows[i] || [];
            if (row.length !== firstRowCols) {
              resolve({ valid: false, error: "Invalid spreadsheet structure" });
              return;
            }
          }
        }
        resolve({ valid: true });
      } catch (err) {
        resolve({ valid: false, error: "File validation failed" });
      }
    };

    reader.onerror = () => {
      resolve({ valid: false, error: "Unable to read file" });
    };

    if (isCsv) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
};

export default function ChatInterface({
  isMobile,
  sidebarOpen,
  setChats,
  chats,
  fetchRecentChats,
}) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isRead = params.get("isRead");

  const [voiceOpen, setVoiceOpen] = useState(false);
  const textRef = useRef("");
  const scrollRef = useRef(null);
  const isFetchingRef = useRef(false);

  const dispatch = useDispatch();
  const { selectedChat, page, sessionId, queryMessage } = useSelector(
    (state) => state.chat
  );

  const textDelayTimeout = useRef(null);
  const [displayedText, setDisplayedText] = useState("");
  const [isContentRendering, setIsContentRendering] = useState(false);
  const [tempId, setTempId] = useState(null);
  const inputRef = useRef(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [inputText, setInputText] = useState("");
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasDocument, setHasDocument] = useState(false);

  const [modelName, setModelName] = useState("gpt-4.1-mini");
  
 const {  chatModels, setModelSelectorConfig } = useOutletContext();

  // Build model options map from API response, with fallback to static options
  const MODEL_OPTIONS =
    chatModels && chatModels.length > 0
      ? chatModels.reduce((acc, model) => {
          if (model?.modelKey && model?.modelName) {
            acc[model.modelKey] = model.modelName;
          }
          return acc;
        }, {})
      : {

        };
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  const handleTextDelay = useCallback((msg) => {
    // Clear any existing timeouts
    if (textDelayTimeout.current) {
      clearTimeout(textDelayTimeout.current);
    }

    setIsContentRendering(true);
    let currentIndex = 0;
    const typingSpeed = 2;
    textRef.current = ""; // Use ref instead of state for intermediate values

    const typeNextCharacter = () => {
      if (currentIndex < msg.length) {
        textRef.current = msg.substring(0, currentIndex + 1);
        setDisplayedText(textRef.current);
        currentIndex += 1;
        textDelayTimeout.current = setTimeout(typeNextCharacter, typingSpeed);
      } else {
        setIsContentRendering(false);
      }
    };

    typeNextCharacter();

    // Cleanup function
    return () => {
      if (textDelayTimeout.current) {
        clearTimeout(textDelayTimeout.current);
      }
      setIsContentRendering(false);
    };
  }, []);
  useLayoutEffect(() => {
    if (location.pathname === "/") {
      dispatch(setSelectedChat({}));
      setChats([]);
      setHasDocument(false);
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const getDoc = async (id) => {
    const res = await getDocumentSessionId(id);

    setHasDocument(res?.data?.document?.length > 0);
  };
  useEffect(() => {
    if (selectedChat?._id) {
      getDoc(selectedChat?._id);
    } else {
      setHasDocument(false);
    }
  }, [selectedChat?._id]);

  useEffect(() => {
    if (
      loading &&
      queryMessage &&
      sessionId &&
      sessionId === selectedChat?._id
    ) {
      setChats((prev) => [...prev, queryMessage]);
    }
    // eslint-disable-next-line
  }, [selectedChat]);

  useEffect(() => {
    if (
      location.pathname !== "/" &&
      selectedChat?._id &&
      (chats?.length === 0 ||
        (chats[chats.length - 1]?._id &&
          !chats[chats.length - 1]._id.includes("temp-user-")))
    ) {
      // Clear existing chats when selecting a new chat
      setChats([]);
      setHasDocument(false);
      handleGetAllChatBySession(selectedChat?._id, 1);
    }
    // eslint-disable-next-line
  }, [selectedChat]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.scrollLeft = inputRef.current.scrollWidth;
    }
  }, [inputText]);

  useEffect(() => {
    if (chats.length > 0) {
      const lastChat = chats[chats.length - 1];
      if (lastChat.role === "assistant" && lastChat.latest_message) {
        handleTextDelay(lastChat.message);
      } else {
        // Immediately show full message if not latest assistant message
        setDisplayedText(lastChat.message);
      }
    }
  }, [chats, handleTextDelay]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && textDelayTimeout.current) {
        clearTimeout(textDelayTimeout.current);
        setIsContentRendering(false);
      } else if (!document.hidden && chats.length > 0) {
        // When returning to the tab, immediately show the full text
        const lastChat = chats[chats.length - 1];
        if (lastChat.role === "assistant" && lastChat.latest_message) {
          setDisplayedText(lastChat.message);
          setIsContentRendering(false);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (textDelayTimeout.current) {
        clearTimeout(textDelayTimeout.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [chats]);

  //  Get all chat by session ID
  const handleGetAllChatBySession = async (id, pageNumber = 1) => {
    trackEvent("FetchChatsBySessionID", { sessionId: id, pageNumber });
    try {
      const data = {
        page: pageNumber,
        limit: 10,
        sessionId: id,
      };
      const res = await getInboxChatBySessionId(data);
      const newChats = res.data.data || [];

      const existingIds = new Set(chats.map((chat) => chat._id));
      const uniqueNewChats = newChats.filter(
        (chat) => !existingIds.has(chat._id)
      );

      // Filter chats to ensure they belong to the current session and have a questionId
      const filteredChats = uniqueNewChats
        .filter((chat) => chat.questionId && chat.sessionId?._id === id)
        .reverse();

      const processedMessages = filteredChats.flatMap((chat) => [
        {
          role: "user",
          message: chat.questionId.message,
          _id: chat.questionId._id,
          ...(chat?.questionId?.media.length > 0 && {
            media: chat?.questionId?.media,
          }),
          sessionId: chat?.sessionId?._id,
          doc: chat?.sessionId?.document?.[0]?._id || null,
          isAudio: chat?.isAudio,
          modelId:getModelData(chatModels, chat?.modelName)?.modelId,
        },
        {
          role: chat.role,
          message: chat.message,
          _id: chat._id,
          sessionId: chat?.sessionId?._id,
          doc: chat?.sessionId?.document?.[0]?._id || null,
          isAudio: chat?.isAudio,
          isDownloadable: chat?.isDownloadble || false,
          fileDownloadLink: chat?.outputUrl,
        },
      ]);

      setChats((prev) => [...processedMessages, ...prev]); // prepend for inverse
      setHasMore(chats.length + processedMessages.length < res.data.total);
    } catch (error) {
      trackException(error);
      trackEvent("FetchChatsBySessionID_Failed", { sessionId: id });
      console.error(error);
    }
  };

  const handleChatResponse = async ({ text, value, media = [] } = {}) => {
    let tempSessionId = selectedChat?._id || isSelectedSessionId?.current?._id;
    setTempId(selectedChat?._id || isSelectedSessionId?.current?._id);
    const data = getModelData(chatModels, modelName);


    const tempUserMessage = {
      role: "user",
      message: text || inputText,
      validateFiles:true,
        modelId:data?.modelId,
       model_name: modelName,
      sessionId: tempSessionId || `temp-user-${Date.now()}`,
      ...(media.length > 0 && { media }),
      doc: media.length > 0 ? `temp-user-${Date.now()}` : null,
    };
    dispatch(setQueryMessage(tempUserMessage));
    dispatch(setChatSessionId(selectedChat?._id));

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 100); // small delay ensures DOM update
    try {
      setLoading(true);

      const textInput = text || inputText;

      if (!textInput.trim()) {
        return;
      }

      // Clear input
      setInputText("");

     

      // Add user message optimistically
      const userMessage = {
        role: "user",
        message: textInput,
         model_name: modelName,
         modelId:data?.modelId,
        _id: `temp-user-${Date.now()}`,
        ...(media.length > 0 && { media }),
        doc: media.length > 0 ? `temp-user-${Date.now()}` : null,
        sessionId: tempSessionId || `temp-user-${Date.now()}`,
        isAudio: voiceOpenRef.current,
      };

      setChats((prev) => [...prev, userMessage]);

      const sanitizedMedia = Array.isArray(media)
        ? media.map((m) => ({ fileUrl: m?.fileUrl, fileName: m?.fileName }))
        : [];

      const payload = {
        message: textInput,
        model_name: modelName,
        validateFiles:true,
        ...((selectedChat?._id || isSelectedSessionId?.current?._id) && {
          sessionId: selectedChat._id || isSelectedSessionId?.current?._id,
        }),
        ...(sanitizedMedia.length > 0 && { media: sanitizedMedia }),
        ...(voiceOpenRef.current && { isAudio: true }),
      };
      const res = await sendChat(payload);
      await handleBotResponse(res);
    } catch (error) {
      handleError(error);
      trackException(error);
      trackEvent("SendMessage_Failed");
    } finally {

      setLoading(false);
    }
  };

  const handleBotResponse = async (res) => {
    const { data } = res;
    if (!data) return;

    const botMessage = {
      role: "assistant",
      message: data?.assistantChat?.message,
      _id: data?.assistantChat?._id,
      doc: data?.userChat?.media[0]?._id || null,
      latest_message: true,
      sessionId: data?.assistantChat?.sessionId,
      isAudio: data?.assistantChat?.isAudio,
      isDownloadable: data?.assistantChat?.isDownloadble || false,
      fileDownloadLink: data?.assistantChat?.outputUrl,
    };
    const resData = await getChatBySessionId(data?.assistantChat?.sessionId);

    if (!selectedChat?._id) {
      selectedChat?._id && setChats([]);
      dispatch(setChatSessionId(""));

      fetchRecentChats();
      const newSessionId = resData?.data?.today?.[0]?._id;
      if (newSessionId) {
        const newPath = `/chat/${newSessionId}`;
        window.history.pushState({}, "", newPath);
      }
      dispatch(setSelectedChat(resData?.data?.today?.[0]));
    }
    setChats((prev) => [...prev, botMessage]);

    if (voiceOpenRef.current) {
      speakReply(data?.assistantChat?.message);
    }
  };

  const handleError = (error) => {
    const errorBotMessage = {
      role: "assistant",
      message: error?.message || "Something went wrong!!",
      _id: `error-bot-${Date.now()}`,
      statusCode: "error",
      isAudio: voiceOpenRef.current,
    };
    trackException(error); trackEvent("SendMessage_Error", { message: error?.message });
    setChats((prev) => [...prev, errorBotMessage]);
  };

  const fetchMoreData = async () => {
    trackEvent("FetchMoreChats_Attempt", { currentPage: page });
    if (isFetchingRef.current || !hasMore) return;

    isFetchingRef.current = true;

    const nextPage = page + 1;
    await handleGetAllChatBySession(selectedChat?._id, nextPage);
    dispatch(setPage(nextPage));

    isFetchingRef.current = false;
  };

  const handleFileChange = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const allowedTypes = [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "image/jpeg",
      "image/png",
    ];
    if (files.length + selectedFiles.length > 3) {
      toast.error("You can only upload up to 3 files in total.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return null;
    }

    const MAX_FILE_SIZE_MB = 10;
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const isValidType = allowedTypes.includes(file.type);
      const isValidSize = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

      if (!isValidType) {
        toast.error(`Unsupported format: ${file.name}`);
        return false;
      }

      if (!isValidSize) {
        toast.error(`File size is too big (Maximum allowed size is 10MB)`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

     // Validate CSV/XLSX structure before upload (consistent column count per row)
    const csvOrXlsxFiles = validFiles.filter(
      (f) =>
        f.type === "text/csv" ||
        f.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
    for (const file of csvOrXlsxFiles) {
      const { valid, error } = await validateCsvOrXlsxFile(file);
      if (!valid) {
        toast.error(error || "Invalid file format");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append("files", file);
      });
      trackEvent("FileUpload_Start", { fileCount: validFiles.length });
      const result = await uploadFiles(formData);

      if (result.statusCode === 200) {
        trackEvent("FileUpload_Success", { uploaded: result.data.length })
        const newFileObjs = result.data.map((url, index) => ({
          fileUrl: url,
          // Use local object URL for instant preview like GenerateImage
          previewUrl: URL.createObjectURL(validFiles[index]),
          fileName: validFiles[index].name,
        }));
        setFiles((prev) => {
          const combined = [...prev, ...newFileObjs].slice(0, 3);
          return combined;
        });
        // Reset file input so re-selecting the same file triggers onChange
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (error) {
      trackException(error);
      trackEvent("FileUpload_Failed");
      console.error("File upload failed:", error);
      toast.error("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => {
      const toRemove = prev[index];
      if (toRemove?.previewUrl) {
        try {
          URL.revokeObjectURL(toRemove.previewUrl);
        } catch (e) {
          // noop
        }
      }
      const next = prev.filter((_, i) => i !== index);
      // Also clear the input to allow selecting the same file again
      if (fileInputRef.current) fileInputRef.current.value = "";
      return next;
    });
  };

  const handleCopyText = (text, messageId) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedMessageId(messageId);
        setTimeout(() => {
          setCopiedMessageId(null);
        }, 1000);
      })
      .catch(() => {
        toast.error("Failed to copy text");
      });
  };

  // Ensure selected model exists in the available image models
  useEffect(() => {
    if (chatModels && chatModels.length > 0) {
      const hasCurrent = chatModels.some(
        (m) => m?.modelKey === modelName
      );
      if (!hasCurrent) {
        setModelName(chatModels[0].modelKey);
      }
    }
  }, [chatModels, modelName]);

   // Register model selector in header
    useEffect(() => {
      // Only set config if we have models available
      if (setModelSelectorConfig && chatModels && chatModels.length > 0 && Object.keys(MODEL_OPTIONS).length > 0) {
        setModelSelectorConfig({
          modelName,
          modelOptions: MODEL_OPTIONS,
          modelList:  chatModels || [],
          onModelChange: setModelName,
        });
      }
      return () => {
        if (setModelSelectorConfig) {
          setModelSelectorConfig(null);
        }
      };

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelName, setModelSelectorConfig,  chatModels]);

  let recognizer = useRef(null);
  let synthesizer = useRef(null);
  let player = useRef(null);
  let audioStop = useRef(null);
  const isClosedRef = useRef(false);
  const isMicOn = useSelector((state) => state.voiceMode.isMicOn);
  const voiceOpenRef = useRef(voiceOpen);
  const isMicOnRef = useRef(isMicOn);
  const isSelectedSessionId = useRef(selectedChat);

  useEffect(() => {
    isSelectedSessionId.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    voiceOpenRef.current = voiceOpen;
  }, [voiceOpen]);

  useEffect(() => {
    isMicOnRef.current = isMicOn;
  }, [isMicOn]);

  const {
    mode,
    stopBackgroundListening,
    startListening,
    startListeningInBackgroundWhileSpeaking,
    stopListening,
    speakReply,
    stopSpeaking,
  } = useVoiceActions({
    isClosedRef,
    handleChatResponse,
    voiceOpenRef,
    synthesizer,
    audioStop,
    player,
    recognizer,
    isMicOnRef,
  });

  const handleClose = () => {
    stopListening();
    stopSpeaking();
    dispatch(resetVoice());
    dispatch(setIsMicOn(false));
    setVoiceOpen(false);
  };

  const handleOpen = () => {
    isClosedRef.current = false;
    isSelectedSessionId.current = [];
    setVoiceOpen(true);
    startListening();
  };

  // Add scroll to bottom function
  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };
  if (isRead === "true") {
    return <ReadOnlyChat chats={chats} />;
  }

  return (
    <div
      className={`flex-1 flex flex-col overflow-scroll sm:overflow-hidden  ${
        isMobile && sidebarOpen ? "opacity-50" : ""
      }`}
    >
      {/* voice mode dialog */}
      <VoiceModeDialog
        open={voiceOpen}
        voiceOpenRef={voiceOpenRef}
        onClose={handleClose}
      >
        <MainContent
          voiceOpen={voiceOpen}
          voiceOpenRef={voiceOpenRef}
          isClosedRef={isClosedRef}
          handleChatResponse={handleChatResponse}
          synthesizer={synthesizer}
          audioStop={audioStop}
          player={player}
          recognizer={recognizer}
          isMicOnRef={isMicOnRef}
        />
        <BottomControls
          mode={mode}
          onMic={() => {
            if (!isMicOn) {
              if (mode === "speaking") {
                dispatch(setIsMicOn(!isMicOn));
                startListeningInBackgroundWhileSpeaking();
              } else if (mode !== "thinking") {
                startListening();
              } else {
                dispatch(setIsMicOn(!isMicOn));
              }
            } else if (isMicOn) {
              if (mode === "speaking") {
                dispatch(setIsMicOn(!isMicOn));
                stopBackgroundListening();
              }
              if (mode !== "thinking") {
                stopListening();
              } else {
                dispatch(setIsMicOn(!isMicOn));
              }
            }
          }}
          onCancel={handleClose}
        />
      </VoiceModeDialog>

      {!selectedChat?._id && chats?.length === 0 && (
        <WelcomeInterface handleChatResponse={handleChatResponse} />
      )}

      {chats?.length > 0 && (
        <div
          id="scrollableDiv"
          ref={scrollRef}
          className="flex-1 py-4 !px-[30px] sm:px-[50px] md:px-[20px] lg:px-[100px] xl:!px-[220px] 2xl:!px-[220px] overflow-y-auto flex flex-col-reverse mt-[40px]"
        >
          <button
            onClick={scrollToBottom}
            className="fixed bottom-36 right-7 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-20"
          >
            <ChevronDown className="w-5 h-5 text-gray-600" />
          </button>
          <InfiniteScroll
            inverse={true} // Key for chat-style
            dataLength={chats.length}
            next={fetchMoreData}
            hasMore={hasMore}
            // loader={<h4>Loading...</h4>}
            scrollableTarget="scrollableDiv"
            loader={<div className="mx-auto" key={0}></div>}
            endMessage={
              chats?.totalPages >= 2 ? (
                <p className="text-center m-2 font-segoeUI">No More chats!</p>
              ) : (
                ""
              )
            }
            scrollThreshold="200px"
            className="overflow-hidden"
          >
            {chats.map((chat, i) =>
              chat.role === "user" ? (
                <div
                  key={chat._id || `chat-${i}`}
                  className={`flex group justify-end ${
                    i === 0 ? "mt-12 mb-8 " : "mt-8 mb-8"
                  }`}
                >
                  <div className="flex flex-row gap-2">
                    <div className="flex flex-col items-end ">
                      <div className="bg-[#1b5c2926] text-[#000000] flex flex-col gap-2 w-fit overflow-x-auto rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] p-[14px] min-h-[44px] justify-center group">
                        <div className="markDownWrapper !leadings-[27px] font-hel leading-[27px] w-fit whitespace-pre-line break-words font-medium text-sm ">
                          <Markdown
                            key={`chat-${chat?._id}`}
                            options={{
                              overrides: {
                                a: { component: CustomLink },
                              },
                            }}
                          >
                            {preprocessText(chat.message)}
                          </Markdown>
                        </div>
                        {chat.media && chat.media.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {chat.media.map((mediaItem, fileIndex) => (
                              <FileCard
                                index={i}
                                key={`uploadfilecard-${mediaItem.fileUrl}`}
                                mediaItem={mediaItem}
                                fileIndex={fileIndex}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                          {chat?.modelId && (
                            <div
                              className="text-[12px] text-gray-500 flex items-center gap-1 ml-3"
                              style={{marginTop: '0px'}}>
                              <span className="ml-2 text-gray-500">
                               <i>Model:{chat.modelId}</i>
                              </span>
                               
                            </div>
                          )}
                        </div>
                      <div className="flex items-center justify-between mt-1">
                        <button
                          onClick={() =>
                            handleCopyText(
                              chat.message,
                              chat._id || `chat-${i}`
                            )
                          }
                          className="flex  cursor-pointer items-center gap-1 text-gray-600 p-1 rounded-md hover:bg-[#1b5c2926] opacity-0 group-hover:opacity-100 transition-opacity text-sm  self-start"
                        >
                          <CustomTooltip
                            bgColor={"#000000"}
                            textColor={"#ffffff"}
                            data-tooltip-id={`copy-${
                              chat._id || `chat-${i}`
                            }-tooltip`}
                            data-tooltip-content={
                              copiedMessageId === (chat._id || `chat-${i}`)
                                ? "Copied!"
                                : "Copy"
                            }
                            delay={0}
                            show={true}
                          >
                            {copiedMessageId === (chat._id || `chat-${i}`) ? (
                              <Check color="#000000B2" size={17} />
                            ) : (
                              <Copy color="#000000B2" size={17} />
                            )}
                          </CustomTooltip>
                        </button>
                        {chat?.isAudio && (
                          <div className="p-1 ">
                            <Mic className="text-[#000000B2]" size={16} />
                          </div>
                        )}
                      </div>
                    </div>
                    {!isMobile && (
                      <div className="!w-[35px] flex-shrink-0">
                        <img
                          src={Avatar}
                          alt="Dabur Logo"
                          className="w-[40px]"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                ((selectedChat?._id && selectedChat?._id === chat.sessionId) ||
                  !selectedChat?._id) && (
                  <div key={chat._id} className="mt-8 mb-8 max-w-4xl space-y-8">
                    <div className="flex gap-4 w-[280px] sm:w-full">
                      {!isMobile && (
                        <div className="w-[40px]">
                          <img
                            src={daburLogo}
                            alt="Dabur Logo"
                            className="w-[40px]"
                          />
                        </div>
                      )}
                      <div className="text-[#000000] flex flex-col gap-2 overflow-x-auto rounded-bl-lg rounded-br-lg rounded-tr-lg min-h-[44px] justify-center w-fit group">
                        <div className="markDownWrapper !leadings-[27px] font-hel w-fit whitespace-pre-line break-words font-medium text-md ">
                          {(() => {
                            let botContent;
                            if (
                              chat?.latest_message &&
                              i === chats?.length - 1
                            ) {
                              botContent = preprocessText(
                                chat?.isAudio ? chat?.message : displayedText
                              );
                            } else {
                              botContent = preprocessText(chat?.message);
                            }
                            return <BotMarkdown content={botContent} />;
                          })()}
                          {chat?.isDownloadable && (
                            <button
                              className={`flex items-center gap-3 py-2 rounded-lg transition `}
                              onClick={() => {
                                if (chat?.fileDownloadLink) {
                                  window.open(chat.fileDownloadLink, "_blank");
                                } else {
                                  toast.error(
                                    "File not available for download"
                                  );
                                }
                              }}
                            >
                              <div className={`bg-white p-3 rounded-full`}>
                                <Download className="w-5 h-5 text-green-800" />
                              </div>
                              <span className="text-base text-[#1B5C29] font-medium">
                                Download File
                              </span>
                            </button>
                          )}

                          <button
                            onClick={() =>
                              handleCopyText(chat.message, chat._id)
                            }
                            className="flex cursor-pointer items-center gap-1 text-gray-600 hover:bg-[#1b5c2926] p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity text-sm mt-2 self-end"
                          >
                            <CustomTooltip
                              bgColor={"#000000"}
                              textColor={"#ffffff"}
                              data-tooltip-id={`copy-${chat._id}-tooltip`}
                              data-tooltip-content={
                                copiedMessageId === chat._id
                                  ? "Copied!"
                                  : "Copy"
                              }
                              show={true}
                              delay={0}
                            >
                              {copiedMessageId === chat._id ? (
                                <Check color="#000000B2" size={17} />
                              ) : (
                                <Copy color="#000000B2" size={17} />
                              )}
                            </CustomTooltip>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )
            )}
            {loading && selectedChat?._id === tempId && <MessageLoader />}
          </InfiniteScroll>
        </div>
      )}

      {chats?.length === 0 && (selectedChat?._id ? <SkeletonLoader /> : <></>)}

      {/* Text Input and File Uploading */}
      <div
        className={classNames(
          "sticky shadow-[0_-4px_20px_rgba(0,0,0,0.08)] bg-[#ECF2EE] bottom-0 mb-0 py-4 !px-[30px] sm:!px-[150px] md:!px-[20px] lg:!px-[200px] xl:!px-[200px] 2xl:!px-[200px]",
          {
            "opacity-50 pointer-events-none":
              loading && selectedChat?._id === tempId,
          }
        )}
      >
        <div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {files.map((file, index) => (
                <UploadFileCard
                  file={file}
                  index={index}
                  key={`uploadfilecard-${file?.fileUrl}}`}
                  removeFile={removeFile}
                />
              ))}
            </div>
          )}
        </div>
        <div
          className="relative w-full !h-[72px] rounded-[14px] !shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]
        "
        >
          <TextInput
            inputRef={inputRef}
            inputText={inputText}
            handleChatResponse={handleChatResponse}
            setInputText={setInputText}
            inputFocused={inputFocused}
            setInputFocused={setInputFocused}
            loading={loading}
            disabled={loading && selectedChat?._id === tempId}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            isUploading={isUploading}
            files={files}
            setFiles={setFiles}
            chats={chats}
            handleOpen={handleOpen}
            isVoiceModeOn={true}
            isContentRendering={isContentRendering}
            hasDocument={hasDocument}
            isMultipleFileUploadEnabled={true}
          />
        </div>
        <div className="max-w-4xl mx-auto mt-[40px]">
          <p className="text-xs text-gray-500 text-center">
            {MESSAGES.POWERED_BY}
          </p>
        </div>
      </div>
    </div>
  );
}

ChatInterface.propTypes = {
  isMobile: PropTypes.bool,
  sidebarOpen: PropTypes.bool,
  setChats: PropTypes.func,
  chats: PropTypes.array,
  fetchRecentChats: PropTypes.func,
};
